/**
 * Shown when a save is rejected by optimistic locking (PRD §6.2).
 *
 * "Conflicting saves are rejected and surfaced to the user, not silently
 * overwritten." A generic "something went wrong" would leave the user retrying
 * the same stale write, so this says what happened, why the edit was not
 * saved, and what to do about it.
 *
 * Reload is the only remedy offered. The current version is deliberately not
 * shown as a number — a version integer means nothing to the person reading it,
 * and the useful action is to see the record as it now stands.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.9, PRD §6.2
 */
import { useTranslation } from 'react-i18next';

import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';

export interface ConflictDialogProps {
  open: boolean;
  onDismiss: () => void;
  /** Reloads the record. Usually a query invalidation, not a page reload. */
  onReload: () => void;
  /** The version the caller submitted, from the problem body. */
  submittedVersion?: number | undefined;
}

export function ConflictDialog({
  open,
  onDismiss,
  onReload,
  submittedVersion,
}: ConflictDialogProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        /* v8 ignore next -- Radix calls this with `true` only when the dialog
           opens itself, which it never does: `open` is a controlled prop. */
        if (!next) onDismiss();
      }}
      title={t('conflict.title')}
      description={t('conflict.body')}
      footer={
        <>
          <Button variant="ghost" onClick={onDismiss}>
            {t('conflict.dismiss')}
          </Button>
          <Button onClick={onReload}>{t('conflict.reload')}</Button>
        </>
      }
    >
      <p className="text-body text-sm">{t('conflict.whatToDo')}</p>

      {/* Shown only when the API reported it. Useful in a bug report, not to
          the user's decision — hence the muted treatment. */}
      {submittedVersion === undefined ? null : (
        <p className="text-gray-400 mt-2 text-xs">
          {t('conflict.submittedVersion', { version: submittedVersion })}
        </p>
      )}
    </Dialog>
  );
}
